/**
 * AppStatusBanner — Immersive, sticky global status banner.
 *
 * Takes over the top of the app with a colored background that extends
 * behind the device status bar. Content below is pushed down (not overlaid).
 *
 * On Android/iOS: StatusBar color matches the banner for a seamless feel.
 * Safe area insets are respected — content doesn't hide behind system icons.
 *
 * Usage:
 *   <AppStatusBanner
 *     color="destructive"
 *     icon={<WarningIcon />}
 *     label="CRITICAL STATUS"
 *     title="Action required"
 *     description="Your account may be compromised."
 *     actions={<Button>Secure Account</Button>}
 *   >
 *     {children} — e.g. task list, checklist items
 *   </AppStatusBanner>
 */

import { useEffect, type ReactNode, type Ref } from "react"
import { Capacitor } from "@capacitor/core"

export type BannerColor = "destructive" | "primary" | "warning" | "success"

/**
 * Global flag: when > 0, a status banner is visible and controls status bar style.
 * syncTheme() in useCapacitorLifecycle should skip status bar updates while this is set.
 */
let activeBannerCount = 0
export function isStatusBannerActive(): boolean {
  return activeBannerCount > 0
}

export interface AppStatusBannerProps {
  /** Skip the inner px-4 py-3 wrapper — caller manages its own padding. */
  noPadding?: boolean
  /** Banner background color theme */
  color?: BannerColor
  /** Status icon (top-left, next to label) */
  icon?: ReactNode
  /** Short uppercase label, e.g. "CRITICAL STATUS" or "IN FOCUS" */
  label?: string
  /** Bold heading */
  title?: string
  /** Description text below title */
  description?: string
  /** Primary/secondary action buttons (bottom of banner) */
  actions?: ReactNode
  /** Inner content — e.g. task chips, checklist items */
  children?: ReactNode
  /** Whether to show the banner (controls mount/unmount externally) */
  visible?: boolean
  /** Ref to the banner's root element (e.g. for measuring its rendered height) */
  ref?: Ref<HTMLDivElement>
}

const COLOR_MAP: Record<BannerColor, { bg: string; text: string; hex: string }> = {
  destructive: {
    bg: "bg-red-700",
    text: "text-white",
    hex: "#b91c1c", // red-700
  },
  primary: {
    bg: "bg-primary",
    text: "text-primary-foreground",
    hex: "", // resolved at runtime from CSS var
  },
  warning: {
    bg: "bg-amber-600",
    text: "text-white",
    hex: "#d97706",
  },
  success: {
    bg: "bg-emerald-700",
    text: "text-white",
    hex: "#047857",
  },
}

/**
 * Resolve the actual hex color for the banner.
 * For "primary", reads from CSS custom property.
 */
function resolveHex(color: BannerColor): string {
  if (color !== "primary") return COLOR_MAP[color].hex
  // Read theme's primary color from CSS
  const root = document.documentElement
  const raw = getComputedStyle(root).getPropertyValue("--primary").trim()
  if (!raw) return "#dc2626" // fallback red if no primary
  // Tailwind v4 uses oklch or hsl — convert to hex via canvas if needed
  // For now, return a reasonable fallback; native status bar handled separately
  return raw.startsWith("#") ? raw : "#2563eb" // blue-600 as safe default
}

export function AppStatusBanner({
  color = "primary",
  icon,
  label,
  title,
  description,
  actions,
  children,
  visible = true,
  noPadding = false,
  ref,
}: AppStatusBannerProps) {
  const scheme = COLOR_MAP[color]

  // Sync Android/iOS status bar color when banner mounts
  useEffect(() => {
    if (!visible) return
    if (!Capacitor.isNativePlatform()) return

    activeBannerCount++
    const hex = resolveHex(color)
    let reverted = false

    void (async () => {
      try {
        const { StatusBar, Style } = await import("@capacitor/status-bar")
        await StatusBar.setBackgroundColor({ color: hex })
        await StatusBar.setStyle({ style: Style.Dark }) // light icons on dark bg
      } catch {
        // StatusBar plugin may not be available in web
      }
    })()

    return () => {
      activeBannerCount--
      if (reverted) return
      reverted = true
      void (async () => {
        try {
          const { StatusBar, Style } = await import("@capacitor/status-bar")
          // Revert to transparent (overlay mode)
          await StatusBar.setBackgroundColor({ color: "#00000000" })
          const isDark = document.documentElement.classList.contains("dark")
          await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light })
        } catch {}
      })()
    }
  }, [visible, color])

  if (!visible) return null

  return (
    <div
      ref={ref}
      className={`${scheme.bg} ${scheme.text} w-full shrink-0`}
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      <div className={noPadding ? "" : "px-4 py-3"}>
        {/* Label row */}
        {(icon || label) && (
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide opacity-90">
            {icon && <span className="size-4 shrink-0">{icon}</span>}
            {label && <span>{label}</span>}
          </div>
        )}

        {/* Title */}
        {title && (
          <h2 className="text-lg font-bold leading-tight">{title}</h2>
        )}

        {/* Description */}
        {description && (
          <p className="mt-1 text-sm leading-snug opacity-90">{description}</p>
        )}

        {/* Children (e.g. task list, checklist) */}
        {children && <div className={icon || label || title || description ? "mt-3" : ""}>{children}</div>}

        {/* Actions row */}
        {actions && <div className="mt-3 flex items-center gap-3">{actions}</div>}
      </div>
    </div>
  )
}
